import React, { useMemo, useCallback } from 'react';
import { Box, Slider, Typography } from '@mui/material';

/**
 * Master timeline scrubber for synchronized video comparison
 * Shows relative time from lap starts (timeline 10,000 = 0:00)
 */
function MasterScrubber({
  timeline,
  sessions = [],
  selectedLaps = new Map(),
  onSeek,
  className = '',
  showDebug = false
}) {
  const TIMELINE_BASELINE = 10000;
  const LEAD_IN_SECONDS = 50; // Allow 50s before lap start
  
  // Calculate timeline range based on session data
  const timelineRange = useMemo(() => {
    if (sessions.length === 0) {
      return { min: TIMELINE_BASELINE - LEAD_IN_SECONDS, max: TIMELINE_BASELINE + 300 }; // Default 5min range
    }
    
    // Find the shortest remaining duration from selected laps
    let minRemainingDuration = Infinity;
    
    sessions.forEach(({ videoId, sessionData }) => {
      if (!sessionData?.lapData) return;
      
      const selectedLap = selectedLaps.get(videoId) || 1;
      const lap = sessionData.lapData.find(l => l.number === selectedLap);
      
      if (lap) {
        // Calculate remaining duration from this lap to end of video
        // Assuming video ends at crossStart + some duration, or use lap duration as estimate
        const remainingDuration = lap.duration || 120; // Default 2min if no duration
        minRemainingDuration = Math.min(minRemainingDuration, remainingDuration);
      }
    });
    
    if (minRemainingDuration === Infinity) {
      minRemainingDuration = 300; // Default 5min
    }
    
    return {
      min: TIMELINE_BASELINE - LEAD_IN_SECONDS,
      max: TIMELINE_BASELINE + minRemainingDuration
    };
  }, [sessions, selectedLaps]);
  
  // Convert timeline time to display time (relative to lap start)
  const formatDisplayTime = useCallback((timelineTime) => {
    const relativeSeconds = timelineTime - TIMELINE_BASELINE;
    const absSeconds = Math.abs(relativeSeconds);
    const minutes = Math.floor(absSeconds / 60);
    const seconds = Math.floor(absSeconds % 60);
    const sign = relativeSeconds < 0 ? '-' : '';
    
    return `${sign}${minutes}:${String(seconds).padStart(2, '0')}`;
  }, []);
  
  // Handle scrubber change
  const handleSliderChange = useCallback((event, newValue) => {
    onSeek?.(newValue);
  }, [onSeek]);
  
  // Calculate marks for lap starts
  const marks = useMemo(() => {
    const baseMarks = [
      {
        value: TIMELINE_BASELINE,
        label: 'Lap Start'
      }
    ];
    
    // Add markers every 30 seconds
    const range = timelineRange.max - timelineRange.min;
    const stepSize = range > 180 ? 60 : 30; // 1min steps for long ranges, 30s for short
    
    for (let offset = -LEAD_IN_SECONDS; offset <= range - LEAD_IN_SECONDS; offset += stepSize) {
      if (offset !== 0) { // Skip the lap start mark we already added
        baseMarks.push({
          value: TIMELINE_BASELINE + offset,
          label: formatDisplayTime(TIMELINE_BASELINE + offset)
        });
      }
    }
    
    return baseMarks;
  }, [timelineRange, formatDisplayTime]);
  
  return (
    <Box className={className} sx={{ p: 2 }}>
      {/* Time Display
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 1
      }}>
        <Typography variant="body2" color="text.secondary">
          Timeline Position
        </Typography>
        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
          {formatDisplayTime(timeline.currentTime)}
        </Typography>
      </Box> */}
      
      {/* Scrubber */}
      <Box sx={{ px: 1 }}>
        <Slider
          value={timeline.currentTime}
          min={timelineRange.min}
          max={timelineRange.max}
          step={0.1}
          marks={marks}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={formatDisplayTime}
          sx={{
            '& .MuiSlider-mark': {
              backgroundColor: 'primary.main',
              height: 8,
              width: 2,
            },
            '& .MuiSlider-markLabel': {
              fontSize: '0.7rem',
              color: 'text.secondary',
            },
            '& .MuiSlider-rail': {
              backgroundColor: 'grey.300',
              height: 6,
            },
            '& .MuiSlider-track': {
              backgroundColor: 'primary.main',
              height: 6,
              border: 'none',
            },
            '& .MuiSlider-thumb': {
              backgroundColor: 'primary.main',
              width: 16,
              height: 16,
              '&:hover': {
                boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.16)',
              },
            },
          }}
        />
      </Box>
      
      {/* Range Info */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mt: 1
      }}>
        <Typography variant="caption" color="text.secondary">
          {formatDisplayTime(timelineRange.min)} (Lead-in)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDisplayTime(timelineRange.max)} (End)
        </Typography>
      </Box>
      
      {/* Debug Info */}
      {showDebug && process.env.NODE_ENV === 'development' && (
        <Typography variant="caption" sx={{ 
          display: 'block', 
          mt: 1, 
          fontFamily: 'monospace',
          color: 'text.secondary'
        }}>
          Raw Timeline: {timeline.currentTime.toFixed(1)}s | 
          Range: {timelineRange.min.toFixed(0)}-{timelineRange.max.toFixed(0)}s
        </Typography>
      )}
    </Box>
  );
}

export default MasterScrubber; 